<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsIcon from '@/components/pds/PdsIcon.vue';
import PdsModal from '@/components/pds/PdsModal.vue';
import { computed, ref, watch } from 'vue';
import { formatDate, formatPickup } from '../format';
import type { PdsOrder, PdsPickupChoice } from '../types';
import InfoNotice from './InfoNotice.vue';
import PickupCalendar from './PickupCalendar.vue';

// La bande montre la semaine à venir ; le calendrier, lui, va jusqu'à un an : le serveur accepte n'importe quelle date.
const DAYS_AHEAD = 7;
const CALENDAR_MONTHS_AHEAD = 12;

const props = defineProps<{
  order: PdsOrder;
  busy?: boolean;
  loadChoices: (date: string) => Promise<PdsPickupChoice[]>;
}>();
const emit = defineEmits<{ close: []; confirm: [pickupTime: number] }>();

const isoDay = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

const days = Array.from({ length: DAYS_AHEAD }, (_, offset) => {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  const label = formatDate(date.getTime());
  return {
    iso: isoDay(date),
    label: offset === 0 ? `Aujourd’hui · ${label.toLowerCase()}` : label,
  };
});

const orderDay = isoDay(new Date(props.order.dueAt));
const day = ref(days.some((d) => d.iso === orderDay) ? orderDay : days[0].iso);
const picked = ref<PdsPickupChoice | null>(null);

// Les créneaux de chaque jour, tels que le serveur les donne : lus ensemble à l'ouverture, comme le fait le client.
const loaded = ref<Record<string, PdsPickupChoice[] | 'error'>>({});
// Les jours se lisent ensemble : la réponse est fusionnée à ce que les autres ont déjà rendu, jamais à une copie prise avant l'attente.
const loadDay = async (iso: string) => {
  let result: PdsPickupChoice[] | 'error';
  try {
    result = await props.loadChoices(iso);
  } catch {
    result = 'error';
  }
  loaded.value = { ...loaded.value, [iso]: result };
};

// Le calendrier : un jour choisi hors de la bande s'y ajoute en pastille, pour qu'on voie toujours le jour affiché.
const calendarOpen = ref(false);
const lastCalendarDay = (() => {
  const date = new Date();
  date.setMonth(date.getMonth() + CALENDAR_MONTHS_AHEAD);
  return isoDay(date);
})();
const farDay = computed(() =>
  days.some((d) => d.iso === day.value)
    ? null
    : {
        iso: day.value,
        label: formatDate(new Date(`${day.value}T00:00`).getTime()),
      },
);
const pickDate = (iso: string) => {
  calendarOpen.value = false;
  day.value = iso;
  if (!(iso in loaded.value)) void loadDay(iso);
};

const hasRoom = (iso: string) => {
  const state = loaded.value[iso];
  return Array.isArray(state) && state.some((choice) => choice.available);
};
// Un jour lu sans aucun créneau libre (fermé, date limite passée, complet) se grise ; un jour en erreur reste cliquable.
const closed = (iso: string) =>
  Array.isArray(loaded.value[iso]) && !hasRoom(iso);

const state = computed(() => loaded.value[day.value]);
const status = computed(() =>
  state.value === undefined
    ? 'loading'
    : state.value === 'error'
      ? 'error'
      : 'ready',
);
const choices = computed(() => (Array.isArray(state.value) ? state.value : []));

// On ouvre sur le premier jour praticable quand celui de la commande ne l'est pas, sauf si l'opérateur a déjà choisi.
const initialDay = day.value;
void Promise.all(days.map((d) => loadDay(d.iso))).then(() => {
  if (day.value !== initialDay || hasRoom(initialDay)) return;
  const open = days.find((d) => hasRoom(d.iso));
  if (open) day.value = open.iso;
});
watch(day, () => {
  picked.value = null;
});

// Choisir l'heure déjà portée par la commande ne changerait rien, mais prévendrait le client.
// Le créneau que la commande porte déjà : reconnaissable dans la grille, et dit pourquoi on ne peut pas le « choisir ».
const isCurrent = (choice: PdsPickupChoice) => choice.at === props.order.dueAt;
const currentShown = computed(() => choices.value.some(isCurrent));
const alreadyCurrent = computed(
  () => !!picked.value && isCurrent(picked.value),
);

const canConfirm = computed(
  () => !!picked.value && picked.value.at !== props.order.dueAt,
);

const confirm = () => {
  if (props.busy || !picked.value || !canConfirm.value) return;
  emit('confirm', picked.value.at);
};
</script>

<template>
  <PdsModal size="lg" :dismissible="!busy" @close="emit('close')">
    <template #title>Changer le retrait</template>
    <template #subtitle>
      #{{ order.orderNumber ?? order.number }} · {{ order.label }} · retrait
      {{ formatPickup(order.dueAt) }}
    </template>

    <div class="flex flex-col gap-[18px] px-5 py-6">
      <p class="text-encre-douce text-[12px] leading-[14px] font-semibold">
        Jour
      </p>
      <div class="flex flex-wrap gap-3">
        <button
          v-for="d in days"
          :key="d.iso"
          type="button"
          :aria-pressed="day === d.iso"
          :disabled="busy || closed(d.iso)"
          class="h-12 cursor-pointer rounded-full px-4 text-[14px] leading-5 font-semibold whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
          :class="[
            day === d.iso ? 'bg-encre text-carte' : 'bg-second text-encre',
            closed(d.iso) && 'line-through',
          ]"
          @click="day = d.iso"
        >
          {{ d.label }}
        </button>
        <button
          v-if="farDay"
          type="button"
          aria-pressed="true"
          :disabled="busy || closed(farDay.iso)"
          class="bg-encre text-carte h-12 cursor-pointer rounded-full px-4 text-[14px] leading-5 font-semibold whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
          :class="closed(farDay.iso) && 'line-through'"
        >
          {{ farDay.label }}
        </button>
        <button
          type="button"
          class="bg-second text-encre flex h-12 cursor-pointer items-center gap-2 rounded-full px-4 text-[14px] leading-5 font-semibold whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
          aria-haspopup="dialog"
          :disabled="busy"
          @click="calendarOpen = !calendarOpen"
        >
          <PdsIcon name="calendar" :size="20" />
          Personnaliser
        </button>
      </div>

      <p class="text-encre-douce text-[12px] leading-[14px] font-semibold">
        Créneaux du commerce
      </p>
      <p v-if="status === 'loading'" class="text-encre-douce text-[14px]">
        Chargement des créneaux…
      </p>
      <div v-else-if="status === 'error'" class="flex items-center gap-3">
        <p role="alert" class="text-alerte-texte text-[14px]">
          Les créneaux n'ont pas pu être chargés.
        </p>
        <PdsButton variant="second" size="md" fit @click="loadDay(day)">
          Réessayer
        </PdsButton>
      </div>
      <p v-else-if="!choices.length" class="text-encre-douce text-[14px]">
        Aucun créneau disponible ce jour-là.
      </p>
      <div v-else class="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        <button
          v-for="choice in choices"
          :key="choice.at"
          type="button"
          :aria-pressed="picked?.at === choice.at"
          :aria-current="isCurrent(choice) ? 'time' : undefined"
          :title="
            isCurrent(choice) ? 'Créneau actuel de la commande' : undefined
          "
          :disabled="busy || !choice.available"
          class="h-12 min-w-0 rounded-full px-4 text-[14px] leading-5 font-semibold whitespace-nowrap"
          :class="[
            !choice.available
              ? 'bg-creux text-filet-fort cursor-not-allowed line-through'
              : picked?.at === choice.at
                ? 'bg-encre text-carte cursor-pointer'
                : 'bg-second text-encre cursor-pointer disabled:cursor-not-allowed disabled:opacity-60',
            isCurrent(choice) && 'ring-encre ring-2 ring-inset',
          ]"
          @click="picked = choice"
        >
          {{ choice.label }}
        </button>
      </div>

      <p class="text-encre-douce text-[12px] leading-[17px]">
        Créneau actuel : {{ formatPickup(order.dueAt) }}
        <template v-if="currentShown">
          · repéré par un cadre dans la grille</template
        >
      </p>

      <InfoNotice title="Le client sera prévenu">
        Il reçoit la nouvelle heure par SMS dès que vous validez.
      </InfoNotice>
    </div>

    <template #footer>
      <div class="flex w-full items-center gap-3 sm:gap-4">
        <PdsButton variant="second" fit :disabled="busy" @click="emit('close')">
          Retour
        </PdsButton>
        <span
          v-if="alreadyCurrent"
          role="status"
          class="text-encre-douce ml-auto text-[12px] leading-[1.45]"
        >
          C'est déjà le créneau actuel
        </span>
        <PdsButton
          :class="alreadyCurrent ? 'max-sm:flex-1' : 'ml-auto max-sm:flex-1'"
          fit
          :loading="busy"
          :disabled="!canConfirm"
          @click="confirm"
        >
          Choisir{{ picked ? ` ${picked.label}` : '' }}
        </PdsButton>
      </div>
    </template>

    <!-- Le calendrier s'ouvre dans sa propre fenêtre : on y choisit une date, ou on le ferme par la croix. -->
    <PdsModal v-if="calendarOpen" size="sm" @close="calendarOpen = false">
      <template #title>
        <span class="flex items-center justify-between gap-3">
          Choisir une date
          <button
            type="button"
            class="bg-second text-encre flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full"
            aria-label="Fermer le calendrier"
            @click="calendarOpen = false"
          >
            <PdsIcon name="close" :size="20" />
          </button>
        </span>
      </template>
      <div class="p-5">
        <PickupCalendar
          :selected="day"
          :min="days[0].iso"
          :max="lastCalendarDay"
          @pick="pickDate"
        />
      </div>
    </PdsModal>
  </PdsModal>
</template>
